import dayjs from 'dayjs';
import { ReceiptsDetailsDTO } from 'dtos';
import { EDate } from 'enums';
import { saveAs } from 'file-saver';
import { ReportsMapper, getRemainingMs, setPersistentLoading, clearPersistentLoading, REPORTS_LOADING_KEY } from 'helpers';
import { IReportsStore, IRootStore } from 'interfaces';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { BaseStore } from './BaseStore';

export class ReportsStore extends BaseStore implements IReportsStore {
  protected persistedReportLoading = false;

  constructor(rootStore: IRootStore) {
    super(rootStore);
    makeObservable<ReportsStore, 'persistedReportLoading'>(this, {
      persistedReportLoading: observable,
      downloadStockSupplier: action.bound,
      downloadStockCarrier: action.bound,
      downloadStockTransactions: action.bound,
      downloadReceiptDetails: action.bound,
      downloadDespatchedLoads: action.bound,
      downloadCarrierAppointments: action.bound,
      isLoadingReport: computed
    });

    const remainingMs = getRemainingMs(REPORTS_LOADING_KEY);
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (remainingMs > 0) {
      runInAction(() => { this.persistedReportLoading = true; });
      timeoutId = setTimeout(() => runInAction(() => {
        this.persistedReportLoading = false;
        clearPersistentLoading(REPORTS_LOADING_KEY);
      }), remainingMs);
    }

    window.addEventListener('storage', (e: StorageEvent) => {
      if (e.key !== REPORTS_LOADING_KEY) {
        return;
      }

      if (e.newValue !== null) {
        runInAction(() => { this.persistedReportLoading = true; });
      } else {
        clearTimeout(timeoutId);
        runInAction(() => { this.persistedReportLoading = false; });
      }
    });
  }

  public async downloadStockSupplier(): Promise<void> {
    if (this.isLoadingReport) {
      return;
    }

    setPersistentLoading(REPORTS_LOADING_KEY);
    this.setLoading('downloadStockSupplier');

    try {
      const owner = ReportsMapper.toStockOwner('SUPPLIER');
      const csvData = await this.rootStore.requester.stockService.downloadStockData(owner);
      const blob = new Blob([csvData], { type: 'data:text/csv;charset=utf-8,' });
      saveAs(blob, `Voorraad - eigenaar leverancier export_stock_${dayjs().format(EDate.YYYY_MM_DD_HHMMSS)}.csv`);

      runInAction(() => {
        this.persistedReportLoading = false;
        clearPersistentLoading(REPORTS_LOADING_KEY);
        this.setSuccess('downloadStockSupplier');
      });
    } catch (e) {
      runInAction(() => {
        this.persistedReportLoading = false;
        clearPersistentLoading(REPORTS_LOADING_KEY);
      });
      this.errorHandler(e);
      this.setError('downloadStockSupplier');
    }
  }

  public async downloadStockCarrier(): Promise<void> {
    if (this.isLoadingReport) {
      return;
    }

    setPersistentLoading(REPORTS_LOADING_KEY);
    this.setLoading('downloadStockCarrier');

    try {
      const params = ReportsMapper.toStockOwner('AHC');
      const csvData = await this.rootStore.requester.stockService.downloadStockData(params);
      const blob = new Blob([csvData], { type: 'data:text/csv;charset=utf-8,' });
      saveAs(blob, `Voorraad - eigenaar Albert Heijn export_stock_${dayjs().format(EDate.YYYY_MM_DD_HHMMSS)}.csv`);

      runInAction(() => {
        this.persistedReportLoading = false;
        clearPersistentLoading(REPORTS_LOADING_KEY);
        this.setSuccess('downloadStockCarrier');
      });
    } catch (e) {
      runInAction(() => {
        this.persistedReportLoading = false;
        clearPersistentLoading(REPORTS_LOADING_KEY);
      });
      this.errorHandler(e);
      this.setError('downloadStockCarrier');
    }
  }

  public async downloadStockTransactions(date: Date): Promise<void> {
    if (this.isLoadingReport) {
      return;
    }

    setPersistentLoading(REPORTS_LOADING_KEY);
    this.setLoading('downloadStockTransactions');

    try {
      const params = ReportsMapper.toStockTransactions(date);
      const csvData = await this.rootStore.requester.stockService.downloadStockTransactions(params);
      const blob = new Blob([csvData], { type: 'data:text/csv;charset=utf-8,' });
      saveAs(
        blob,
        `Voorraadmutaties - eigenaar Leverancier export_stock_transactions_${dayjs().format(
          EDate.YYYY_MM_DD_HHMMSS
        )}.csv`
      );

      runInAction(() => {
        this.persistedReportLoading = false;
        clearPersistentLoading(REPORTS_LOADING_KEY);
        this.setSuccess('downloadStockTransactions');
      });
    } catch (e) {
      runInAction(() => {
        this.persistedReportLoading = false;
        clearPersistentLoading(REPORTS_LOADING_KEY);
      });
      this.errorHandler(e);
      this.setError('downloadStockTransactions');
    }
  }

  public async downloadReceiptDetails(date: Date, reference: string): Promise<void> {
    if (this.isLoadingReport) {
      return;
    }

    setPersistentLoading(REPORTS_LOADING_KEY);
    this.setLoading('downloadReceiptDetails');

    try {
      const filters = ReportsMapper.toReceiptDetails(date, reference);
      const csvData = await this.rootStore.requester.reportsService.downloadReports<ReceiptsDetailsDTO>(2, filters);
      const blob = new Blob([csvData], { type: 'data:text/csv;charset=utf-8,' });
      saveAs(
        blob,
        `Voorraadmutaties - eigenaar Leverancier export_stock_transactions_${dayjs().format(
          EDate.YYYY_MM_DD_HHMMSS
        )}.csv`
      );

      runInAction(() => {
        this.persistedReportLoading = false;
        clearPersistentLoading(REPORTS_LOADING_KEY);
        this.setSuccess('downloadReceiptDetails');
      });
    } catch (e) {
      runInAction(() => {
        this.persistedReportLoading = false;
        clearPersistentLoading(REPORTS_LOADING_KEY);
      });
      this.errorHandler(e);
      this.setError('downloadReceiptDetails');
    }
  }

  public async downloadDespatchedLoads(startDate: Date, endDate: Date): Promise<void> {
    if (this.isLoadingReport) {
      return;
    }

    setPersistentLoading(REPORTS_LOADING_KEY);
    this.setLoading('downloadStockTransactions');

    try {
      const filters = ReportsMapper.toDespatchedLoads(startDate, endDate);
      const csvData = await this.rootStore.requester.reportsService.downloadReports(3, filters);
      const blob = new Blob([csvData], { type: 'data:text/csv;charset=utf-8,' });
      saveAs(
        blob,
        `Voorraadmutaties - eigenaar Leverancier export_stock_transactions_${dayjs().format(
          EDate.YYYY_MM_DD_HHMMSS
        )}.csv`
      );

      runInAction(() => {
        this.persistedReportLoading = false;
        clearPersistentLoading(REPORTS_LOADING_KEY);
        this.setSuccess('downloadStockTransactions');
      });
    } catch (e) {
      runInAction(() => {
        this.persistedReportLoading = false;
        clearPersistentLoading(REPORTS_LOADING_KEY);
      });
      this.errorHandler(e);
      this.setError('downloadStockTransactions');
    }
  }

  public async downloadCarrierAppointments(startDate: Date, endDate: Date): Promise<void> {
    if (this.isLoadingReport) {
      return;
    }

    setPersistentLoading(REPORTS_LOADING_KEY);
    this.setLoading('downloadCarrierAppointments');

    try {
      const csvData = await this.rootStore.requester.appointmentsService.downloadAppointments(
        dayjs(startDate).format(EDate.YYYY_MM_DD),
        dayjs(endDate).format(EDate.YYYY_MM_DD)
      );
      const blob = new Blob([csvData], { type: 'data:text/csv;charset=utf-8,' });
      saveAs(blob, `Carrier Appointment - ${dayjs().format(EDate.YYYY_MM_DD_HHMMSS)}.csv`);

      runInAction(() => {
        this.persistedReportLoading = false;
        clearPersistentLoading(REPORTS_LOADING_KEY);
        this.setSuccess('downloadCarrierAppointments');
      });
    } catch (e) {
      runInAction(() => {
        this.persistedReportLoading = false;
        clearPersistentLoading(REPORTS_LOADING_KEY);
      });
      this.errorHandler(e);
      this.setError('downloadCarrierAppointments');
    }
  }

  public get isLoadingReport(): boolean {
    return (
      this.persistedReportLoading ||
      this.getAsyncStatus('downloadStockSupplier').loading ||
      this.getAsyncStatus('downloadStockCarrier').loading ||
      this.getAsyncStatus('downloadStockTransactions').loading ||
      this.getAsyncStatus('downloadReceiptDetails').loading ||
      this.getAsyncStatus('downloadCarrierAppointments').loading
    );
  }
}
